function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function getPackageRemainingMs(pkg = {}) {
  const ms = toMillis(pkg?.expiresAt);
  if (!ms) return 0;
  return Math.max(0, ms - Date.now());
}

export function getPackageCountdownData(pkg = {}) {
  const expiryMs = toMillis(pkg?.expiresAt);
  const remainingMs = getPackageRemainingMs(pkg);
  const totalMinutes = Math.floor(remainingMs / (60 * 1000));
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  const isExpired = !expiryMs || isPackageExpired(pkg) || remainingMs <= 0;

  const short =
    isExpired
      ? "Expired"
      : days > 0
        ? `${days}d ${hours}h left`
        : hours > 0
          ? `${hours}h ${minutes}m left`
          : `${minutes}m left`;

  const detailed = isExpired
    ? "Package expired"
    : `${days}d ${hours}h ${minutes}m remaining`;

  return {
    expiryMs,
    remainingMs,
    days,
    hours,
    minutes,
    isExpired,
    short,
    detailed,
  };
}

export function isPackageExpired(pkg = {}) {
  if (!pkg) return true;
  if (String(pkg.status || "").toLowerCase() === "expired") return true;
  const ms = toMillis(pkg.expiresAt);
  return !!ms && ms < Date.now();
}

export function normalizeActivePackages(userDoc = {}) {
  const rawPackages = Array.isArray(userDoc?.activeStorePackages)
    ? userDoc.activeStorePackages
    : [];

  const fallback =
    rawPackages.length === 0 && userDoc?.storePackage?.packageId
      ? [userDoc.storePackage]
      : [];

  return [...rawPackages, ...fallback]
    .filter((pkg) => pkg?.packageId)
    .map((pkg) => ({
      ...pkg,
      expired: isPackageExpired(pkg),
    }))
    .filter((pkg, index, arr) => {
      const firstIndex = arr.findIndex((entry) => {
        const sameId = entry.packageId === pkg.packageId;
        const sameExpiry = toMillis(entry.expiresAt) === toMillis(pkg.expiresAt);
        const sameSource = String(entry.source || "") === String(pkg.source || "");
        return sameId && sameExpiry && sameSource;
      });
      return firstIndex === index;
    });
}

export function summarizeStorePackages(userDoc = {}) {
  const allPackages = normalizeActivePackages(userDoc);
  const activePackages = allPackages.filter((pkg) => !pkg.expired);
  const sourcePackages = activePackages.length > 0 ? activePackages : allPackages;

  const summary = sourcePackages.reduce(
    (acc, pkg) => {
      acc.courseLimit += Number(pkg.courseLimit || 0);
      acc.productLimit += Number(pkg.productLimit || 0);
      acc.serviceLimit += Number(pkg.serviceLimit || 0);
      acc.totalPackages += 1;

      const expiry = toMillis(pkg.expiresAt);
      if (expiry > acc.latestExpiryMs) {
        acc.latestExpiryMs = expiry;
        acc.expiresAt = pkg.expiresAt || null;
      }
      return acc;
    },
    {
      courseLimit: 0,
      productLimit: 0,
      serviceLimit: 0,
      totalPackages: 0,
      latestExpiryMs: 0,
      expiresAt: null,
    },
  );

  const latestPackage =
    sourcePackages
      .slice()
      .sort((a, b) => toMillis(b.expiresAt) - toMillis(a.expiresAt))[0] || null;

  return {
    allPackages,
    activePackages,
    latestPackage,
    hasActivePackage: activePackages.length > 0,
    hasAnyPackage: allPackages.length > 0,
    summary: {
      packageId:
        latestPackage?.packageId ||
        userDoc?.storePackage?.packageId ||
        "",
      name:
        activePackages.length > 1
          ? `${activePackages.length} active packages`
          : latestPackage?.name || userDoc?.storePackage?.name || "",
      courseLimit: summary.courseLimit,
      productLimit: summary.productLimit,
      serviceLimit: summary.serviceLimit,
      expiresAt: summary.expiresAt,
      totalPackages: summary.totalPackages,
      status:
        activePackages.length > 0 ? "active" : allPackages.length > 0 ? "expired" : "inactive",
    },
  };
}

export function inferStoreBusinessType(userDoc = {}, items = []) {
  const stored = String(
    userDoc?.storeBusinessType ||
      userDoc?.storeMode ||
      userDoc?.businessType ||
      "",
  )
    .trim()
    .toLowerCase();

  if (["product", "course", "service"].includes(stored)) return stored;

  const firstItem = items
    .slice()
    .sort((a, b) => toMillis(a.createdAt) - toMillis(b.createdAt))[0];

  const inferred = String(firstItem?.type || "").trim().toLowerCase();
  return ["product", "course", "service"].includes(inferred) ? inferred : "";
}

export function getBusinessTypeLabel(type = "") {
  if (type === "product") return "Shopping Complex";
  if (type === "course") return "University";
  if (type === "service") return "Services";
  return "Not selected";
}
